import { useCallback, useContext, useState } from "react";
import { AuthContext } from "../../../auth/Auth";
import { requestAccountDeletionAPIUrl } from "../../../stripe/config.js";

/**
 * Calls POST /requestAccountDeletion. Not `fetchData`: it flattens errors to a
 * string, and a 409 `team_member` needs its own "contact support" state.
 */
export function useDeleteAccount() {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  const deleteAccount = useCallback(async (): Promise<void> => {
    if (!currentUser) throw new Error("Not signed in");
    setLoading(true);
    setError(null);
    setBlocked(false);
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(requestAccountDeletionAPIUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        if (data?.error?.reason === "team_member") setBlocked(true);
        throw new Error(
          data?.error?.message || `Request failed (HTTP ${resp.status})`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  return { deleteAccount, loading, error, blocked };
}
